import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { API_BASE } from '../config';

export interface PlayerSummary {
  playerId: string;
  playerName: string;
  lastSaved: string;
  basicOre: number;
  fleetSize: number;
}

export interface AdminSaveDto {
  playerId: string;
  gameStateJson: string;
  lastModified: string;
}

let adminApiKey: string | null = null;

export function setAdminApiKey(key: string) {
  if (!key) {
    adminApiKey = null;
    sessionStorage.removeItem('adminApiKey');
    return;
  }
  adminApiKey = key;
  sessionStorage.setItem('adminApiKey', key);
}

export function getAdminApiKey(): string | null {
  if (!adminApiKey) {
    adminApiKey = sessionStorage.getItem('adminApiKey');
  }
  return adminApiKey;
}

export function hasAdminApiKey(): boolean {
  return !!getAdminApiKey();
}

const resolveBaseUrl = () => {
  try {
    const apiUrl = new URL(API_BASE, window.location.origin);
    apiUrl.pathname = apiUrl.pathname.replace(/\/api\/v\d+\/?$/, '');
    return apiUrl.href.replace(/\/$/, '');
  } catch {
    const normalized = API_BASE.replace(/\/api\/v\d+\/?$/, '');
    if (normalized.startsWith('http')) {
      return normalized.replace(/\/$/, '');
    }
    return `${window.location.origin}${normalized.replace(/\/$/, '')}`;
  }
};

const normalizeHubUrl = (baseUrl: string, path: string, keepBasePath: boolean) => {
  const url = new URL(baseUrl || window.location.origin);
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const basePath = keepBasePath ? url.pathname.replace(/\/$/, '') : '';
  url.pathname = `${basePath}${normalizedPath}` || normalizedPath;
  url.search = '';
  url.hash = '';
  return url.toString().replace(/\/$/, '');
};

const buildHubUrlCandidates = () => {
  const baseUrl = resolveBaseUrl() || window.location.origin;
  const candidates = new Set<string>();
  const paths = ['/adminHub', '/fleet-api/adminHub'];

  paths.forEach((path) => {
    candidates.add(normalizeHubUrl(baseUrl, path, true));
    candidates.add(normalizeHubUrl(baseUrl, path, false));
  });

  return Array.from(candidates);
};

export const getAdminHubUrlCandidates = () => buildHubUrlCandidates();

export const createAdminHubConnection = (hubUrl?: string) => {
  const candidates = getAdminHubUrlCandidates();
  const url = hubUrl ?? candidates[0];
  if (!url) {
    throw new Error('Unable to determine admin hub endpoint');
  }

  console.debug('[adminService] Connecting to admin hub:', url);

  return new HubConnectionBuilder()
    .withUrl(url)
    .withAutomaticReconnect()
    .configureLogging(LogLevel.Information)
    .build();
};

export const authenticateAdmin = async (connection: HubConnection, apiKey: string) => {
  const success = await connection.invoke<boolean>('Authenticate', apiKey);
  if (success) {
    setAdminApiKey(apiKey);
  }
  return success;
};

const ensureAuthenticated = (connection: HubConnection) => {
  if (!connection || connection.state !== 'Connected') {
    throw new Error('Admin hub not connected');
  }
  if (!hasAdminApiKey()) {
    throw new Error('Admin API key missing');
  }
};

export const adminGetAllPlayers = async (connection: HubConnection): Promise<PlayerSummary[]> => {
  ensureAuthenticated(connection);
  return await connection.invoke<PlayerSummary[]>('GetPlayers');
};

export const adminGetPlayerSave = async (connection: HubConnection, playerId: string): Promise<AdminSaveDto> => {
  ensureAuthenticated(connection);
  return await connection.invoke<AdminSaveDto>('GetPlayerSave', playerId);
};

export const adminUpdatePlayerSave = async (connection: HubConnection, dto: AdminSaveDto) => {
  ensureAuthenticated(connection);
  return await connection.invoke('UpdatePlayerSave', dto.playerId, dto.gameStateJson);
};
